import { DesignerPickerItemResource } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources';
import { ImageHashPreviewSize } from 'dy-frontend-shared/lib/data/valueObjects/ImageHashPreview/enums';
import React from 'react';

import Avatar from '@app/components/Avatar';
import DevText from '@app/components/Text';
import { imageHashPreview } from '@app/data/consts';
import { Button, FormGroup, Intent, MenuItem, Spinner, Tag } from '@blueprintjs/core';
import { Suggest } from '@blueprintjs/select';
import Flex from '@components/Flex';

import { designerFullName, matchesDesignerQuery } from '../../../utils';

export interface DesignerPickerFieldProps {
    items: DesignerPickerItemResource[] | null;
    selected: DesignerPickerItemResource | null;
    disabled?: boolean;
    onSelect: (item: DesignerPickerItemResource) => void;
    onClear: () => void;
}

export type Props = DesignerPickerFieldProps;

const avatarSrcOf = (imageHash: ImageHash | null) =>
    imageHash ? imageHashPreview.userImage(imageHash, ImageHashPreviewSize.XS) : null;

// Exactly one designer assignment, chosen from one searchable field.
//
// Clearing only resets this modal's selection; it does not change Request participation or history.
// The Assigned Designer remains required until another designer is selected.
//
// One control rather than a card plus a separate Change button: the previous shape hid the
// roster behind an extra click and offered no way to search it, which is unusable once a
// Request has more than a screenful of assignable designers.
const DesignerPickerField: React.FC<Props> = ({ items, selected, disabled = false, onSelect, onClear }) => {
    // `is_participant: false` means assigning this designer will also add them to the parent
    // Request. Surfacing it here is what stops that being a silent side effect of the save.
    const renderParticipationTag = (item: DesignerPickerItemResource, className: string) => {
        if (item.is_participant) {
            return null;
        }

        return (
            <Tag className={className} minimal intent={Intent.WARNING}>
                Will be added to the Request
            </Tag>
        );
    };

    const renderItem = (item: DesignerPickerItemResource, { handleClick, modifiers }) => {
        if (!modifiers.matchesPredicate) {
            return null;
        }

        return (
            <MenuItem
                key={item.user.id}
                active={modifiers.active}
                selected={selected !== null && selected.user.id === item.user.id}
                onClick={handleClick}
                text={
                    <Flex direction="row" align="center">
                        <Avatar
                            className="mr-1"
                            width="28px"
                            height="28px"
                            alt={designerFullName(item)}
                            src={avatarSrcOf(item.user.image_hash)}
                        />

                        <Flex direction="column" align="flex-start" justify="center">
                            <DevText>{designerFullName(item)}</DevText>
                            {renderParticipationTag(item, '')}
                        </Flex>
                    </Flex>
                }
            />
        );
    };

    // Loading and "loaded and empty" are different answers and are shown differently, exactly
    // as they were before: neither is a designer the caller may pick
    const renderNoResults = () => {
        if (!items) {
            return (
                <MenuItem
                    disabled
                    text={
                        <Flex justify="center">
                            <Spinner size={20} />
                        </Flex>
                    }
                />
            );
        }

        return <MenuItem disabled text="No assignable designers found" />;
    };

    // The editable input carries the selected name. This compact summary keeps the avatar and
    // participation disclosure visible without turning the combobox back into a button target.
    const renderSelectedSummary = () => {
        if (!selected) {
            return null;
        }

        return (
            <Flex className="mt-1" direction="row" align="center">
                <Avatar
                    className="mr-1"
                    width="28px"
                    height="28px"
                    alt={designerFullName(selected)}
                    src={avatarSrcOf(selected.user.image_hash)}
                />

                <DevText inline muted>
                    Selected:&nbsp;
                </DevText>
                <DevText inline>{designerFullName(selected)}</DevText>

                <Button
                    minimal
                    small
                    type="button"
                    icon="cross"
                    intent={Intent.DANGER}
                    disabled={disabled}
                    aria-label="Clear selected designer"
                    title="Clear selected designer"
                    onClick={onClear}
                />

                {renderParticipationTag(selected, 'ml-1')}
            </Flex>
        );
    };

    return (
        <FormGroup label="Designer">
            <Suggest<DesignerPickerItemResource>
                disabled={disabled}
                fill
                // Blueprint Suggest is an actual input target. With openOnKeyDown=false, mouse
                // focus or keyboard TAB opens the full list immediately; typing then narrows it.
                openOnKeyDown={false}
                resetOnClose
                items={items ?? []}
                itemPredicate={(query, item) => matchesDesignerQuery(item, query)}
                itemRenderer={renderItem}
                noResults={renderNoResults()}
                inputValueRenderer={designerFullName}
                selectedItem={selected}
                inputProps={{ leftIcon: 'search', placeholder: 'Search designers' }}
                onItemSelect={onSelect}
                menuProps={{ className: 'custom-thin-scroll' }}
                popoverProps={{
                    minimal: true,
                    matchTargetWidth: true,
                }}
            />
            {renderSelectedSummary()}
        </FormGroup>
    );
};

export default DesignerPickerField;
